// src/components/admin/ReportsModule.tsx
// DROP 42B — ERUE Festival Suite Reports Module
// Copyright © Digitrax Finteca Consulting — https://digitraxfinteca.com
// All platform activity reports with PDF / CSV / DOCX / Print export

import React, { useState, useCallback } from "react";
import {
  BarChart3, Download, FileText, RefreshCw,
  Users, ShoppingCart, Ticket, DollarSign, Building2,
  Heart, TrendingUp, AlertTriangle, CheckCircle,
} from "lucide-react";
import { jsPDF } from "jspdf";
import { Document, Packer, Paragraph, Table, TableRow, TableCell,
         TextRun, HeadingLevel, AlignmentType, WidthType, BorderStyle } from "docx";
import { saveAs } from "file-saver";
import { printHtml } from "../../utils/printUtils";
import PrintButton from "../shared/PrintButton";

// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
  bg: "#070D0A", surface: "#0E1812", card: "#121E16", border: "#1C2E22",
  text: "#EDE8DC", muted: "#9CA38F",
  gold: "#C9A84C", goldLt: "#E2C97E",
  green: "#0F5132", greenLt: "#4ADE80",
  red: "#ef4444", orange: "#f97316", blue: "#60a5fa", purple: "#A78BFA",
};

// ── Auth ──────────────────────────────────────────────────────────────────────
const authHdr = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("erue_auth_token") || sessionStorage.getItem("erue_auth_token") || ""}`,
});

// ── Formatters ────────────────────────────────────────────────────────────────
const fmtN  = (n: number) => "₦" + (n || 0).toLocaleString("en-NG");
const fmtD  = (s: string) => s ? new Date(s).toLocaleDateString("en-NG") : "—";
const fmtDT = (s: string) => s ? new Date(s).toLocaleString("en-NG") : "—";

// ── Copyright / branding strings ──────────────────────────────────────────────
const COPYRIGHT = "© Digitrax Finteca Consulting | digitraxfinteca.com | support@digitraxfinteca.com";
const FESTIVAL  = "ERUE Festival 2026 — Uzairue Kingdom, Etsako West LGA, Edo State, Nigeria";
const MOTTO     = '"One People, One Kingdom, One Destiny"';

// ── Report types ──────────────────────────────────────────────────────────────
type ReportId =
  | "registrations" | "voting" | "store" | "raffle"
  | "budget"        | "accounting" | "vendors" | "fundraising";

interface ReportDef {
  id: ReportId; label: string; icon: React.ReactNode;
  endpoint: string; color: string; description: string;
}

const REPORTS: ReportDef[] = [
  { id: "registrations", label: "Event Registrations", icon: <Users size={14}/>,
    endpoint: "/api/admin/event-registrations",
    color: C.blue,    description: "All festival event registrations with status" },
  { id: "voting",        label: "Contestants & Voting", icon: <CheckCircle size={14}/>,
    endpoint: "/api/admin/contestants",
    color: C.purple,  description: "Miss ERUE contestants and vote tallies" },
  { id: "store",         label: "Store Orders",    icon: <ShoppingCart size={14}/>,
    endpoint: "/api/admin/store/orders",
    color: C.gold,    description: "Festival store purchases and revenue" },
  { id: "raffle",        label: "Raffle Tickets",  icon: <Ticket size={14}/>,
    endpoint: "/api/raffle/data",
    color: C.orange,  description: "Raffle ticket sales and prize status" },
  { id: "budget",        label: "Budget Plans",    icon: <DollarSign size={14}/>,
    endpoint: "/api/admin/budget/plans",
    color: C.greenLt, description: "All budget plans with variance analysis" },
  { id: "accounting",    label: "Accounting",      icon: <TrendingUp size={14}/>,
    endpoint: "/api/accounting/transactions",
    color: "#4ADE80", description: "Income and expenditure transactions" },
  { id: "vendors",       label: "Vendor Applications", icon: <Building2 size={14}/>,
    endpoint: "/api/vendor/admin/applications",
    color: C.goldLt,  description: "Vendor registrations and payment status" },
  { id: "fundraising",   label: "Fundraising",     icon: <Heart size={14}/>,
    endpoint: "/api/fundraising/admin/donations",
    color: "#f472b6", description: "Donations and fundraising campaigns" },
];

// ── Props ─────────────────────────────────────────────────────────────────────
interface CurrentUser { email?: string; name?: string; systemRole?: string; role?: string; }
interface Props { currentUser?: CurrentUser | null; }

// ── PDF generator ─────────────────────────────────────────────────────────────
function generatePdf(title: string, columns: string[], rows: string[][]): void {
  const doc = new jsPDF({ orientation: rows.length > 20 ? "landscape" : "portrait", unit: "mm", format: "a4" });
  const pw = doc.internal.pageSize.getWidth();
  let y = 15;

  // Header
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.setTextColor(15, 81, 50);
  doc.text("ERUE FESTIVAL 2026", pw / 2, y, { align: "center" }); y += 7;
  doc.setFontSize(11);
  doc.setTextColor(201, 168, 76);
  doc.text(title.toUpperCase(), pw / 2, y, { align: "center" }); y += 5;
  doc.setDrawColor(201, 168, 76);
  doc.setLineWidth(0.5);
  doc.line(10, y, pw - 10, y); y += 6;
  doc.setFontSize(8);
  doc.setTextColor(100, 100, 100);
  doc.text(FESTIVAL, pw / 2, y, { align: "center" }); y += 5;
  doc.text(`Generated: ${new Date().toLocaleString("en-NG")}`, pw / 2, y, { align: "center" }); y += 8;

  // Table
  const colW = (pw - 20) / columns.length;
  const rowH = 7;

  // Header row
  doc.setFillColor(15, 81, 50);
  doc.rect(10, y, pw - 20, rowH, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(7);
  doc.setTextColor(255, 255, 255);
  columns.forEach((col, i) => {
    doc.text(col.toUpperCase().slice(0, 18), 12 + i * colW, y + 4.5);
  });
  y += rowH;

  // Data rows
  doc.setFont("helvetica", "normal");
  rows.forEach((row, ri) => {
    if (y > doc.internal.pageSize.getHeight() - 20) {
      doc.addPage();
      y = 15;
    }
    if (ri % 2 === 0) {
      doc.setFillColor(245, 245, 245);
      doc.rect(10, y, pw - 20, rowH, "F");
    }
    doc.setTextColor(30, 30, 30);
    row.forEach((cell, i) => {
      doc.text(String(cell || "—").slice(0, 20), 12 + i * colW, y + 4.5);
    });
    y += rowH;
  });

  // Footer
  doc.setFontSize(7);
  doc.setTextColor(150, 150, 150);
  const fh = doc.internal.pageSize.getHeight() - 10;
  doc.line(10, fh - 5, pw - 10, fh - 5);
  doc.text(MOTTO, pw / 2, fh - 2, { align: "center" });
  doc.text(COPYRIGHT, pw / 2, fh + 2, { align: "center" });

  doc.save(`ERUE-${title.replace(/\s+/g, "-")}-${Date.now()}.pdf`);
}

// ── CSV generator ─────────────────────────────────────────────────────────────
function generateCsv(title: string, columns: string[], rows: string[][]): void {
  const escape = (v: string) => `"${String(v || "").replace(/"/g, '""')}"`;
  const lines = [
    `# ERUE Festival 2026 — ${title}`,
    `# ${FESTIVAL}`,
    `# Generated: ${new Date().toLocaleString("en-NG")}`,
    `# ${COPYRIGHT}`,
    "",
    columns.map(escape).join(","),
    ...rows.map(row => row.map(escape).join(",")),
  ];
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  saveAs(blob, `ERUE-${title.replace(/\s+/g, "-")}-${Date.now()}.csv`);
}

// ── DOCX generator ───────────────────────────────────────────────────────────
async function generateDocx(title: string, columns: string[], rows: string[][]): Promise<void> {
  const headerCells = columns.map(col =>
    new TableCell({
      children: [new Paragraph({ children: [new TextRun({ text: col.toUpperCase(), bold: true, color: "FFFFFF", size: 18 })], alignment: AlignmentType.CENTER })],
      shading: { fill: "0F5132" },
      borders: { top: { style: BorderStyle.SINGLE, size: 1 }, bottom: { style: BorderStyle.SINGLE, size: 1 }, left: { style: BorderStyle.SINGLE, size: 1 }, right: { style: BorderStyle.SINGLE, size: 1 } },
    })
  );

  const dataRows = rows.map((row, ri) =>
    new TableRow({
      children: row.map(cell =>
        new TableCell({
          children: [new Paragraph({ children: [new TextRun({ text: String(cell || "—"), size: 16 })] })],
          shading: { fill: ri % 2 === 0 ? "F5F5F5" : "FFFFFF" },
          borders: { top: { style: BorderStyle.SINGLE, size: 1 }, bottom: { style: BorderStyle.SINGLE, size: 1 }, left: { style: BorderStyle.SINGLE, size: 1 }, right: { style: BorderStyle.SINGLE, size: 1 } },
        })
      ),
    })
  );

  const doc = new Document({
    sections: [{
      children: [
        new Paragraph({ text: "ERUE FESTIVAL 2026", heading: HeadingLevel.HEADING_1, alignment: AlignmentType.CENTER }),
        new Paragraph({ text: title, heading: HeadingLevel.HEADING_2, alignment: AlignmentType.CENTER }),
        new Paragraph({ children: [new TextRun({ text: FESTIVAL, color: "555555", size: 18 })], alignment: AlignmentType.CENTER }),
        new Paragraph({ children: [new TextRun({ text: `Generated: ${new Date().toLocaleString("en-NG")}`, color: "888888", size: 16 })], alignment: AlignmentType.CENTER }),
        new Paragraph({ text: "" }),
        new Table({
          width: { size: 100, type: WidthType.PERCENTAGE },
          rows: [new TableRow({ children: headerCells, tableHeader: true }), ...dataRows],
        }),
        new Paragraph({ text: "" }),
        new Paragraph({ children: [new TextRun({ text: MOTTO, italics: true, color: "0F5132" })], alignment: AlignmentType.CENTER }),
        new Paragraph({ children: [new TextRun({ text: COPYRIGHT, color: "888888", size: 16 })], alignment: AlignmentType.CENTER }),
      ],
    }],
  });

  const buffer = await Packer.toBlob(doc);
  saveAs(buffer, `ERUE-${title.replace(/\s+/g, "-")}-${Date.now()}.docx`);
}

// ── Print report ──────────────────────────────────────────────────────────────
function printReport(title: string, columns: string[], rows: string[][], summary?: Record<string, string>): void {
  const summaryHtml = summary
    ? `<div style="display:flex;gap:16px;flex-wrap:wrap;margin:12px 0">
        ${Object.entries(summary).map(([k, v]) =>
          `<div style="background:#f5f0e0;border:1px solid #C9A84C;border-radius:6px;padding:8px 14px;min-width:120px">
            <div style="font-size:9px;color:#888;text-transform:uppercase;letter-spacing:1px;margin-bottom:3px">${k}</div>
            <div style="font-size:14px;font-weight:700;color:#0F5132">${v}</div>
          </div>`).join("")}
      </div>` : "";

  const tableRows = rows.map((row, i) =>
    `<tr>${row.map(cell => `<td style="padding:6px 10px;border-bottom:1px solid #eee;font-size:11px;background:${i % 2 === 0 ? "#f9f9f9" : "#fff"}">${cell || "—"}</td>`).join("")}</tr>`
  ).join("");

  const html = `<!DOCTYPE html><html><head><meta charset="UTF-8">
    <style>
      body{font-family:'Segoe UI',Arial,sans-serif;padding:20px;max-width:900px;margin:0 auto;color:#111;font-size:12px}
      .hdr{text-align:center;margin-bottom:16px}
      .hdr h1{font-size:20px;color:#0F5132;margin:0;font-family:Georgia,serif}
      .hdr h2{font-size:14px;color:#C9A84C;margin:4px 0;text-transform:uppercase;letter-spacing:2px}
      .hdr p{font-size:10px;color:#888;margin:2px 0}
      .divider{border:none;border-top:2px solid #C9A84C;margin:10px 0}
      table{width:100%;border-collapse:collapse;margin-top:10px}
      th{background:#0F5132;color:#fff;padding:8px 10px;text-align:left;font-size:10px;text-transform:uppercase;letter-spacing:0.5px}
      .footer{margin-top:20px;text-align:center;font-size:9px;color:#aaa;border-top:1px solid #eee;padding-top:10px}
      @media print{.no-print{display:none}}
    </style>
  </head><body>
    <div class="hdr">
      <h1>ERUE FESTIVAL 2026</h1>
      <h2>${title}</h2>
      <p>${FESTIVAL}</p>
      <p>Generated: ${new Date().toLocaleString("en-NG")}</p>
    </div>
    <hr class="divider">
    ${summaryHtml}
    <table>
      <thead><tr>${columns.map(c => `<th>${c}</th>`).join("")}</tr></thead>
      <tbody>${tableRows}</tbody>
    </table>
    <div class="footer">
      <p>${MOTTO}</p>
      <p>${COPYRIGHT}</p>
      <p>Printed: ${new Date().toLocaleString("en-NG")}</p>
    </div>
  </body></html>`;

  printHtml(html, `ERUE — ${title}`);
}

// ── Export toolbar ────────────────────────────────────────────────────────────
function ExportBar({ title, columns, rows, summary, loading }: {
  title: string; columns: string[]; rows: string[][];
  summary?: Record<string, string>; loading: boolean;
}) {
  const [docxBusy, setDocxBusy] = useState(false);
  const dis = loading || rows.length === 0;

  return (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
      <span style={{ fontSize: 11, color: C.muted }}>{rows.length} record{rows.length !== 1 ? "s" : ""}</span>
      <button disabled={dis} onClick={() => generateCsv(title, columns, rows)}
        style={{ display: "flex", alignItems: "center", gap: 5, padding: "6px 12px",
          background: dis ? C.border : "#4ADE8022", border: `1px solid ${dis ? C.border : "#4ADE8044"}`,
          borderRadius: 6, color: dis ? C.muted : "#4ADE80", fontSize: 11, fontWeight: 700, cursor: dis ? "not-allowed" : "pointer" }}>
        <Download size={11}/> CSV
      </button>
      <button disabled={dis} onClick={() => generatePdf(title, columns, rows)}
        style={{ display: "flex", alignItems: "center", gap: 5, padding: "6px 12px",
          background: dis ? C.border : `${C.red}22`, border: `1px solid ${dis ? C.border : `${C.red}44`}`,
          borderRadius: 6, color: dis ? C.muted : C.red, fontSize: 11, fontWeight: 700, cursor: dis ? "not-allowed" : "pointer" }}>
        <FileText size={11}/> PDF
      </button>
      <button disabled={dis || docxBusy} onClick={async () => { setDocxBusy(true); try { await generateDocx(title, columns, rows); } finally { setDocxBusy(false); } }}
        style={{ display: "flex", alignItems: "center", gap: 5, padding: "6px 12px",
          background: dis ? C.border : `${C.blue}22`, border: `1px solid ${dis ? C.border : `${C.blue}44`}`,
          borderRadius: 6, color: dis ? C.muted : C.blue, fontSize: 11, fontWeight: 700, cursor: dis ? "not-allowed" : "pointer" }}>
        {docxBusy ? <RefreshCw size={11} style={{ animation: "spin 1s linear infinite" }}/> : <Download size={11}/>} DOCX
      </button>
      <PrintButton label="Print" size="sm" variant="ghost" disabled={dis}
        onClick={() => printReport(title, columns, rows, summary)} />
    </div>
  );
}

// ── Stat card ─────────────────────────────────────────────────────────────────
function Stat({ label, value, color }: { key?: React.Key; label: string; value: string; color?: string }) {
  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8,
      padding: "12px 16px", flex: 1, minWidth: 120 }}>
      <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700, color: color || C.gold, fontFamily: "Cinzel,serif" }}>{value}</div>
    </div>
  );
}

// ── Table renderer ────────────────────────────────────────────────────────────
function ReportTable({ columns, rows, loading, error }: {
  columns: string[]; rows: string[][]; loading: boolean; error: string | null;
}) {
  if (loading) return (
    <div style={{ textAlign: "center", padding: 40, color: C.muted }}>
      <RefreshCw size={24} style={{ animation: "spin 1s linear infinite", margin: "0 auto 10px", display: "block" }}/>
      <div style={{ fontSize: 13 }}>Loading report data...</div>
    </div>
  );
  if (error) return (
    <div style={{ textAlign: "center", padding: 32, background: `${C.red}11`, border: `1px solid ${C.red}33`,
      borderRadius: 8, color: C.red, fontSize: 13 }}>
      <AlertTriangle size={24} style={{ margin: "0 auto 10px", display: "block" }}/>{error}
    </div>
  );
  if (rows.length === 0) return (
    <div style={{ textAlign: "center", padding: 40, color: C.muted, fontSize: 13 }}>
      No data found. Click "Load Report" to fetch data.
    </div>
  );
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
        <thead>
          <tr>
            {columns.map(col => (
              <th key={col} style={{ background: C.green, color: "#fff", padding: "8px 10px",
                textAlign: "left", fontSize: 10, textTransform: "uppercase",
                letterSpacing: 0.5, whiteSpace: "nowrap" }}>{col}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>
              {row.map((cell, j) => (
                <td key={j} style={{ padding: "7px 10px", borderBottom: `1px solid ${C.border}`,
                  color: C.text, background: i % 2 === 0 ? C.card : C.surface,
                  whiteSpace: "nowrap", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis" }}>
                  {cell || "—"}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Data transformers ─────────────────────────────────────────────────────────
function transformRegistrations(data: Record<string, unknown>[]) {
  const cols = ["#", "Name", "Type", "Email", "Phone", "Village", "Status", "Date"];
  const rows = data.map((r, i) => [
    String(i + 1),
    String(r.fullName || r.name || "—"),
    String(r.registrationType || r.type || "—"),
    String(r.email || "—"),
    String(r.phone || r.phoneNumber || "—"),
    String(r.village || r.community || "—"),
    String(r.status || "pending"),
    fmtD(String(r.submittedAt || r.createdAt || "")),
  ]);
  const summary = {
    "Total": String(data.length),
    "Approved": String(data.filter(r => r.status === "approved").length),
    "Pending": String(data.filter(r => r.status === "pending").length),
    "Rejected": String(data.filter(r => r.status === "rejected").length),
    "Miss ERUE": String(data.filter(r => r.registrationType === "miss-erue" || r.type === "miss-erue").length),
  };
  return { cols, rows, summary };
}

function transformVoting(data: Record<string, unknown>[]) {
  const cols = ["#", "Name", "Village", "Total Votes", "Status", "Registered"];
  const rows = data.map((c, i) => [
    String(i + 1),
    String(c.name || "—"),
    String(c.village || c.community || "—"),
    String(c.totalVotes || c.votes || "0"),
    String(c.status || "—"),
    fmtD(String(c.createdAt || "")),
  ]);
  const totalVotes = data.reduce((s, c) => s + Number(c.totalVotes || c.votes || 0), 0);
  const summary = {
    "Contestants": String(data.length),
    "Total Votes": String(totalVotes.toLocaleString()),
    "Approved": String(data.filter(c => c.status === "approved").length),
  };
  return { cols, rows, summary };
}

function transformStore(data: Record<string, unknown>[]) {
  const cols = ["#", "Reference", "Customer", "Email", "Amount", "Items", "Status", "Date"];
  const rows = data.map((o, i) => [
    String(i + 1),
    String(o.reference || o.ref || "—"),
    String(o.name || o.customerName || "—"),
    String(o.email || "—"),
    fmtN(Number(o.amount || o.total || 0)),
    String(Array.isArray(o.items) ? o.items.length : "—"),
    String(o.status || "—"),
    fmtD(String(o.createdAt || o.paidAt || "")),
  ]);
  const revenue = data.filter(o => o.status === "paid" || o.status === "success")
    .reduce((s, o) => s + Number(o.amount || o.total || 0), 0);
  const summary = {
    "Total Orders": String(data.length),
    "Revenue": fmtN(revenue),
    "Paid": String(data.filter(o => o.status === "paid" || o.status === "success").length),
    "Pending": String(data.filter(o => o.status === "pending").length),
  };
  return { cols, rows, summary };
}

function transformRaffle(data: Record<string, unknown>) {
  const tickets = (data.tickets as Record<string, unknown>[]) || [];
  const cols = ["#", "Ticket No.", "Holder Name", "Phone", "Email", "Status", "Date"];
  const rows = tickets.map((t, i) => [
    String(i + 1),
    String(t.ticketNumber || "—"),
    String(t.holderName || "—"),
    String(t.holderPhone || "—"),
    String(t.holderEmail || "—"),
    String(t.status || "active"),
    fmtD(String(t.purchasedAt || t.createdAt || "")),
  ]);
  const summary = {
    "Total Tickets": String(tickets.length),
    "Active": String(tickets.filter(t => t.status !== "won").length),
    "Won": String(tickets.filter(t => t.status === "won").length),
  };
  return { cols, rows, summary };
}

function transformBudget(data: Record<string, unknown>[]) {
  const cols = ["#", "Title", "Level", "Status", "Budgeted", "Actual", "Est. Revenue", "Variance"];
  const rows = data.map((p, i) => {
    const v = Number(p.totalBudgeted || 0) - Number(p.totalActual || 0);
    return [
      String(i + 1),
      String(p.title || "—"),
      String(p.level || "—").toUpperCase(),
      String(p.status || "—"),
      fmtN(Number(p.totalBudgeted || 0)),
      fmtN(Number(p.totalActual || 0)),
      fmtN(Number(p.totalEstimatedRevenue || 0)),
      (v >= 0 ? "+" : "") + fmtN(v),
    ];
  });
  const totalB = data.reduce((s, p) => s + Number(p.totalBudgeted || 0), 0);
  const totalA = data.reduce((s, p) => s + Number(p.totalActual || 0), 0);
  const summary = {
    "Plans": String(data.length),
    "Active": String(data.filter(p => p.status === "active").length),
    "Total Budgeted": fmtN(totalB),
    "Total Actual": fmtN(totalA),
    "Net Variance": (totalB - totalA >= 0 ? "+" : "") + fmtN(totalB - totalA),
  };
  return { cols, rows, summary };
}

function transformAccounting(data: Record<string, unknown>[]) {
  const cols = ["#", "Date", "Type", "Category", "Description", "Amount", "Reference"];
  const rows = data.map((t, i) => [
    String(i + 1),
    fmtD(String(t.date || t.createdAt || "")),
    String(t.type || "—"),
    String(t.category || "—"),
    String(t.description || "—"),
    fmtN(Number(t.amount || 0)),
    String(t.reference || t.ref || "—"),
  ]);
  const income = data.filter(t => t.type === "income").reduce((s, t) => s + Number(t.amount || 0), 0);
  const expense = data.filter(t => t.type !== "income").reduce((s, t) => s + Number(t.amount || 0), 0);
  const summary = {
    "Transactions": String(data.length),
    "Total Income": fmtN(income),
    "Total Expense": fmtN(expense),
    "Net Balance": fmtN(income - expense),
  };
  return { cols, rows, summary };
}

function transformVendors(data: Record<string, unknown>[]) {
  const cols = ["#", "Business Name", "Contact", "Email", "Phone", "Category", "Status", "Date"];
  const rows = data.map((v, i) => [
    String(i + 1),
    String(v.businessName || v.name || "—"),
    String(v.contactPerson || v.contact || "—"),
    String(v.email || "—"),
    String(v.phone || "—"),
    String(v.category || "—"),
    String(v.status || "pending"),
    fmtD(String(v.submittedAt || v.createdAt || "")),
  ]);
  const summary = {
    "Total": String(data.length),
    "Approved": String(data.filter(v => v.status === "approved").length),
    "Pending": String(data.filter(v => v.status === "pending").length),
    "Rejected": String(data.filter(v => v.status === "rejected").length),
  };
  return { cols, rows, summary };
}

function transformFundraising(data: Record<string, unknown>[]) {
  const cols = ["#", "Donor Name", "Email", "Phone", "Amount", "Campaign", "Status", "Date"];
  const rows = data.map((d, i) => [
    String(i + 1),
    String(d.donorName || d.name || "—"),
    String(d.email || "—"),
    String(d.phone || "—"),
    fmtN(Number(d.amount || 0)),
    String(d.campaign || d.campaignTitle || "General"),
    String(d.status || "—"),
    fmtD(String(d.donatedAt || d.createdAt || "")),
  ]);
  const total = data.reduce((s, d) => s + Number(d.amount || 0), 0);
  const summary = {
    "Total Donors": String(data.length),
    "Total Raised": fmtN(total),
    "Confirmed": String(data.filter(d => d.status === "confirmed" || d.status === "paid").length),
  };
  return { cols, rows, summary };
}

// ── Main component ────────────────────────────────────────────────────────────
export default function ReportsModule({ currentUser }: Props) {
  const [activeReport, setActiveReport] = useState<ReportId>("registrations");
  const [data,         setData]         = useState<Record<string, unknown>[] | Record<string, unknown>>([]);
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState<string | null>(null);
  const [loaded,       setLoaded]       = useState<ReportId | null>(null);

  const role    = ((currentUser?.systemRole || currentUser?.role || "") as string).toUpperCase();
  const isAdmin = ["PLATFORM_ADMIN", "FESTIVAL_COORDINATOR", "ACCOUNTANT"].includes(role);

  const report  = REPORTS.find(r => r.id === activeReport)!;

  const loadReport = useCallback(async () => {
    setLoading(true); setError(null); setData([]);
    try {
      const res  = await fetch(report.endpoint, { headers: authHdr() });
      const json = await res.json();
      if (json.success === false && json.error) throw new Error(json.error);
      // Handle different response shapes
      const raw =
        json.registrations || json.contestants || json.orders ||
        json.applications  || json.transactions || json.donations ||
        json.plans         || json.items        || json;
      setData(Array.isArray(raw) ? raw : (typeof raw === "object" ? raw : []));
      setLoaded(activeReport);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to load report");
    } finally { setLoading(false); }
  }, [report.endpoint, activeReport]);

  // Derive table data
  const { cols, rows, summary } = (() => {
    const d = Array.isArray(data) ? data as Record<string, unknown>[] : [];
    const dObj = (!Array.isArray(data) && typeof data === "object") ? data as Record<string, unknown> : {};
    if (loaded !== activeReport) return { cols: [], rows: [], summary: {} };
    switch (activeReport) {
      case "registrations": return transformRegistrations(d);
      case "voting":        return transformVoting(d);
      case "store":         return transformStore(d);
      case "raffle":        return transformRaffle(dObj);
      case "budget":        return transformBudget(d);
      case "accounting":    return transformAccounting(d);
      case "vendors":       return transformVendors(d);
      case "fundraising":   return transformFundraising(d);
      default:              return { cols: [], rows: [], summary: {} };
    }
  })();

  // Guard
  if (!currentUser) return (
    <div style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center", background: C.bg }}>
      <div style={{ textAlign: "center", padding: "40px 32px", background: C.card, border: `1px solid ${C.gold}33`, borderRadius: 16, maxWidth: 380 }}>
        <BarChart3 size={48} style={{ color: C.gold, margin: "0 auto 16px" }}/>
        <h2 style={{ color: C.gold, fontFamily: "Cinzel,serif", marginBottom: 8, fontSize: 18 }}>Reports Module</h2>
        <p style={{ color: C.muted, fontSize: 13, lineHeight: 1.6 }}>Staff login required to access ERUE Festival Reports.</p>
      </div>
    </div>
  );

  if (!isAdmin) return (
    <div style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center", background: C.bg }}>
      <div style={{ textAlign: "center", padding: "40px 32px", background: C.card, border: `1px solid ${C.red}33`, borderRadius: 16, maxWidth: 380 }}>
        <AlertTriangle size={48} style={{ color: C.red, margin: "0 auto 16px" }}/>
        <h2 style={{ color: C.red, fontFamily: "Cinzel,serif", marginBottom: 8, fontSize: 18 }}>Access Restricted</h2>
        <p style={{ color: C.muted, fontSize: 13, lineHeight: 1.6 }}>Reports are available to Accountants, Festival Coordinators, and Platform Administrators.</p>
      </div>
    </div>
  );

  return (
    <div style={{ background: C.bg, minHeight: "80vh", paddingBottom: 40 }}>

      {/* ── Header ── */}
      <div style={{ background: C.surface, borderBottom: `1px solid ${C.border}`, padding: "20px 24px 0" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, flexWrap: "wrap", gap: 8 }}>
          <div>
            <h2 style={{ fontSize: 20, fontWeight: 700, color: C.gold, fontFamily: "Cinzel,serif", margin: 0 }}>
              📊 Reports Module
            </h2>
            <p style={{ fontSize: 12, color: C.muted, margin: "4px 0 0" }}>
              ERUE Festival 2026 · DROP 42B · Export: PDF / CSV / DOCX / Print
            </p>
          </div>
          <div style={{ fontSize: 10, color: C.muted, textAlign: "right" }}>
            <div>{COPYRIGHT}</div>
          </div>
        </div>

        {/* ── Report type tabs ── */}
        <div style={{ display: "flex", gap: 2, overflowX: "auto", paddingBottom: 1 }}>
          {REPORTS.map(r => (
            <button key={r.id} onClick={() => { setActiveReport(r.id); setLoaded(null); setData([]); setError(null); }}
              style={{ display: "flex", alignItems: "center", gap: 5, padding: "8px 14px",
                background: "none", border: "none",
                borderBottom: activeReport === r.id ? `2px solid ${r.color}` : "2px solid transparent",
                color: activeReport === r.id ? r.color : C.muted,
                fontSize: 12, fontWeight: activeReport === r.id ? 700 : 400,
                cursor: "pointer", whiteSpace: "nowrap" }}>
              {r.icon}{r.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Body ── */}
      <div style={{ padding: "24px" }}>

        {/* ── Report header ── */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start",
          flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
          <div>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: report.color,
              fontFamily: "Cinzel,serif", margin: "0 0 4px" }}>
              {report.icon} {report.label} Report
            </h3>
            <p style={{ fontSize: 12, color: C.muted, margin: 0 }}>{report.description}</p>
          </div>
          <button onClick={loadReport} disabled={loading}
            style={{ display: "flex", alignItems: "center", gap: 6, padding: "9px 18px",
              background: report.color, border: "none", borderRadius: 7,
              color: "#070D0A", fontSize: 12, fontWeight: 700, cursor: loading ? "not-allowed" : "pointer",
              opacity: loading ? 0.7 : 1 }}>
            {loading
              ? <><RefreshCw size={13} style={{ animation: "spin 1s linear infinite" }}/> Loading...</>
              : <><RefreshCw size={13}/> Load Report</>}
          </button>
        </div>

        {/* ── Summary cards ── */}
        {loaded === activeReport && Object.keys(summary).length > 0 && (
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
            {Object.entries(summary).map(([k, v]) => (
              <Stat key={k} label={k} value={v} color={report.color} />
            ))}
          </div>
        )}

        {/* ── Export toolbar ── */}
        {loaded === activeReport && rows.length > 0 && (
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
            <ExportBar title={report.label} columns={cols} rows={rows} summary={summary} loading={loading} />
          </div>
        )}

        {/* ── Table ── */}
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, overflow: "hidden" }}>
          <ReportTable columns={cols} rows={rows} loading={loading} error={error} />
        </div>

        {/* ── Not loaded state ── */}
        {!loading && !error && loaded !== activeReport && (
          <div style={{ textAlign: "center", padding: "48px 24px",
            background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, color: C.muted,
            fontSize: 13, marginTop: 0 }}>
            <BarChart3 size={40} style={{ margin: "0 auto 12px", opacity: 0.3, display: "block" }}/>
            <div>Click <strong style={{ color: report.color }}>"Load Report"</strong> to fetch {report.label.toLowerCase()} data.</div>
            <div style={{ fontSize: 11, marginTop: 6 }}>Exports available after loading: PDF · CSV · DOCX · Print</div>
          </div>
        )}

        {/* ── Footer branding ── */}
        <div style={{ marginTop: 32, textAlign: "center", fontSize: 10, color: C.muted }}>
          <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 12 }}>
            <div style={{ color: C.gold, fontStyle: "italic", marginBottom: 4 }}>{MOTTO}</div>
            <div>{FESTIVAL}</div>
            <div style={{ marginTop: 4 }}>{COPYRIGHT}</div>
          </div>
        </div>

      </div>
      <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}
